// Tippspiel API client.
// Replaces the Supabase JS client. Uses cookies (httpOnly) for auth, so
// every request forwards them automatically and there's nothing to store
// client-side.
//
// In dev, vite proxies /api and /sse to http://localhost:3000.
// In prod, the API is served from the same origin as the SPA.

export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(status: number, message: string, data: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

async function request<T>(method: string, path: string, body?: unknown, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { ...(init?.headers as any) };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers["content-type"] = "application/json";
    payload = JSON.stringify(body);
  }
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers,
    body: payload,
    ...init,
  });

  // 401 → try one silent refresh, then retry once.
  //
  // Earlier this excluded "/api/auth/*" entirely to avoid an infinite
  // refresh loop on the refresh endpoint itself. That over-shot: it also
  // skipped `/api/auth/me`, which is the FIRST request on app boot. So a
  // returning user whose 1-hour access token had expired would be told
  // they were logged out — even though their 30-day refresh token was
  // still valid. End result: users had to re-login every time they
  // reopened the PWA after lunch.
  //
  // The skip list now only covers the endpoints where 401 is either
  // expected (wrong password) or would cause a real refresh loop.
  const skipRefreshOn401 = new Set([
    "/api/auth/refresh",
    "/api/auth/login",
    "/api/auth/signup",
    "/api/auth/logout",
    "/api/auth/magic/request",
    "/api/auth/magic/verify",
    "/api/auth/reset/request",
    "/api/auth/reset/perform",
  ]);
  if (res.status === 401 && !skipRefreshOn401.has(path)) {
    const refresh = await fetch("/api/auth/refresh", { method: "POST", credentials: "include" });
    if (refresh.ok) {
      return request<T>(method, path, body, init);
    }
  }

  if (!res.ok) {
    let data: unknown = null;
    try {
      data = await res.json();
    } catch {
      /* ignore */
    }
    const msg = (data as any)?.error ?? res.statusText ?? "request_failed";
    throw new ApiError(res.status, msg, data);
  }
  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

export const http = {
  get: <T = unknown>(path: string) => request<T>("GET", path),
  post: <T = unknown>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T = unknown>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T = unknown>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T = unknown>(path: string, body?: unknown) => request<T>("DELETE", path, body),
  upload: <T = unknown>(path: string, file: File, fieldName = "file") => {
    const fd = new FormData();
    fd.append(fieldName, file);
    return request<T>("POST", path, fd);
  },
};

// ---- Domain types (subset — the rest live in @/types/db) ----
export interface Profile {
  id: string;
  username: string;
  emoji: string;
  level: number;
  xp: number;
  streak: number;
  bestStreak: number;
  avatarUrl: string | null;
  bonusWinner: string | null;
  hasPassword: boolean;
  onboardingCompleted: boolean;
  tourSeen: boolean;
  lastTipDate: string | null;
  streakFreezeUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MeResponse {
  user: { id: string; email: string; roles: string[] };
  profile: Profile | null;
}

// ---- API surface (typed wrappers) ----
export const api = {
  auth: {
    me: () => http.get<MeResponse>("/api/auth/me"),
    signup: (body: { email: string; password: string; username?: string; emoji?: string }) =>
      http.post<{ ok: true; user: { id: string; email: string; username: string } }>("/api/auth/signup", body),
    login: (body: { email: string; password: string }) => http.post("/api/auth/login", body),
    logout: () => http.post("/api/auth/logout"),
    refresh: () => http.post("/api/auth/refresh"),
    requestMagic: (body: { email: string; redirect?: string; purpose?: "login" | "signup" }) =>
      http.post("/api/auth/magic/request", body),
    verifyMagic: (token: string) => http.post<{ ok: true; redirect: string | null }>("/api/auth/magic/verify", { token }),
    requestReset: (email: string) => http.post("/api/auth/reset/request", { email }),
    performReset: (token: string, password: string) => http.post("/api/auth/reset/perform", { token, password }),
    setPassword: (password: string) => http.post("/api/auth/password", { password }),
  },

  profiles: {
    me: () => http.get<MeResponse>("/api/auth/me").then((r) => r.profile),
    list: (ids?: string[]) => http.get<Profile[]>(ids?.length ? `/api/profiles?ids=${ids.join(",")}` : "/api/profiles"),
    byId: (id: string) => http.get<Profile>(`/api/profiles/${id}`),
    byUsername: (username: string) => http.get<Profile>(`/api/profiles/by-username/${encodeURIComponent(username)}`),
    usernameAvailable: (username: string) =>
      http.get<{ available: boolean }>(`/api/profiles/username-available/${encodeURIComponent(username)}`),
    update: (data: Partial<Pick<Profile, "username" | "emoji" | "bonusWinner" | "onboardingCompleted" | "tourSeen">>) =>
      http.patch<Profile>("/api/profiles/me", data),
    uploadAvatar: (file: File) => http.upload<{ url: string }>("/api/profiles/me/avatar", file),
    deleteAvatar: () => http.del("/api/profiles/me/avatar"),
  },

  games: {
    list: (filter?: { phase?: string; group?: string }) => {
      const q = new URLSearchParams();
      if (filter?.phase) q.set("phase", filter.phase);
      if (filter?.group) q.set("group", filter.group);
      const qs = q.toString();
      return http.get<any[]>(`/api/games${qs ? `?${qs}` : ""}`);
    },
    byId: (id: string) => http.get<any>(`/api/games/${id}`),
    adminUpdate: (id: string, data: any) => http.patch<any>(`/api/admin/games/${id}`, data),
    adminCreate: (data: {
      homeTeam: string;
      awayTeam: string;
      homeFlag?: string;
      awayFlag?: string;
      kickoffTime: string;
      groupLabel?: string;
      phase?: string;
      venue?: string | null;
    }) => http.post<any>("/api/admin/games", data),
    adminDelete: (id: string) => http.del<{ ok: true; id: string }>(`/api/admin/games/${id}`),
    adminCleanupTests: () => http.post<{ ok: true; deleted: number }>("/api/admin/games/cleanup-test"),
    adminSearchFixtures: (params: { team?: string; date?: string; league?: number; season?: number }) => {
      const q = new URLSearchParams();
      if (params.team) q.set("team", params.team);
      if (params.date) q.set("date", params.date);
      if (params.league) q.set("league", String(params.league));
      if (params.season) q.set("season", String(params.season));
      return http.get<{ fixtures: ApiFootballFixture[]; error?: string }>(`/api/admin/fixtures/search?${q.toString()}`);
    },
    adminImportFixture: (fixtureId: number) =>
      http.post<any>("/api/admin/games/import-fixture", { fixtureId }),
  },

  tips: {
    listMine: () => http.get<any[]>("/api/tips?mine=true"),
    listForUser: (userId: string) => http.get<any[]>(`/api/tips?userId=${userId}`),
    // LEGACY full list (O(tippers) payload) — only for the lazy group-tab
    // heatmap which needs per-tip userIds. Hot paths use summary().
    listForGame: (gameId: string) => http.get<any[]>(`/api/tips/by-game/${gameId}`),
    summary: (gameId: string) => http.get<TipSummary>(`/api/tips/by-game/${gameId}/summary`),
    upsert: (gameId: string, homeTip: number, awayTip: number) =>
      http.put<any>("/api/tips", { gameId, homeTip, awayTip }),
    delete: (id: string) => http.del(`/api/tips/${id}`),
  },

  rankings: {
    // LEGACY full-array endpoint — payload grows with user count. Prefer
    // `top()` for anything user-facing; this stays for stale clients.
    global: () => http.get<any[]>("/api/rankings/global"),
    // Slim ranking: top-N rows (or exact ids) + own row + total count.
    top: (opts?: { limit?: number; ids?: string[] }) => {
      const q = new URLSearchParams();
      if (opts?.limit != null) q.set("limit", String(opts.limit));
      if (opts?.ids?.length) q.set("ids", opts.ids.join(","));
      const qs = q.toString();
      return http.get<{ rows: any[]; total: number; me: any | null }>(
        `/api/rankings/top${qs ? `?${qs}` : ""}`,
      );
    },
    groups: () => http.get<any[]>("/api/rankings/groups"),
    liveBonus: () => http.get<LiveBonus>("/api/rankings/live-bonus"),
  },

  groups: {
    list: () => http.get<any[]>("/api/groups"),
    mine: () => http.get<any[]>("/api/groups/mine"),
    bySlug: (slug: string) => http.get<any>(`/api/groups/by-slug/${slug}`),
    lookup: (code: string) => http.get<any>(`/api/groups/lookup/${code}`),
    nameAvailable: (name: string) =>
      http.get<{ available: boolean }>(`/api/groups/name-available/${encodeURIComponent(name)}`),
    create: (name: string, description?: string) => http.post<any>("/api/groups", { name, description }),
    claim: (code: string) => http.post<{ groupId: string }>(`/api/groups/claim/${code}`),
    requestJoin: (args: { code?: string; groupId?: string }) =>
      http.post<{ groupId: string; name: string; status: string }>("/api/groups/request-join", args),
    listRequests: (groupId: string) => http.get<any[]>(`/api/groups/${groupId}/requests`),
    approveRequest: (requestId: string) => http.post(`/api/groups/requests/${requestId}/approve`),
    rejectRequest: (requestId: string) => http.post(`/api/groups/requests/${requestId}/reject`),
    leave: (groupId: string) => http.post(`/api/groups/${groupId}/leave`),
    regenerateCode: (groupId: string) => http.post<{ code: string }>(`/api/groups/${groupId}/regenerate-code`),
    setMemberRole: (groupId: string, userId: string, role: "admin" | "member") =>
      http.post(`/api/groups/${groupId}/members/${userId}/role`, { role }),
    removeMember: (groupId: string, userId: string) =>
      http.del<{ ok: true }>(`/api/groups/${groupId}/members/${userId}`),
    uploadLogo: (groupId: string, file: File) => http.upload<{ url: string }>(`/api/groups/${groupId}/logo`, file),
  },

  bonus: {
    list: () => http.get<any[]>("/api/bonus"),
    me: () => http.get<any>("/api/bonus/me"),
    truth: () => http.get<any>("/api/bonus/truth"),
    upsert: (data: BonusTipUpsert) => http.put<any>("/api/bonus/me", data),
    adminResolve: (data: BonusResolve) => http.post("/api/admin/bonus/resolve", data),
  },

  feed: {
    list: (limit = 50) => http.get<any[]>(`/api/feed?limit=${limit}`),
    create: (message: string) => http.post<any>("/api/admin/feed", { message }),
  },

  badges: {
    list: (userId?: string) => http.get<any[]>(`/api/badges${userId ? `?userId=${userId}` : ""}`),
  },

  players: {
    list: (filter?: { team?: string; search?: string }) => {
      const q = new URLSearchParams();
      if (filter?.team) q.set("team", filter.team);
      if (filter?.search) q.set("search", filter.search);
      return http.get<any[]>(`/api/players${q.toString() ? `?${q}` : ""}`);
    },
  },

  trikot: {
    me: () => http.get<any>("/api/trikot/me"),
    redeem: () => http.post<any>("/api/trikot/me"),
    // Self-collect — user marks their own redemption as collected.
    collectMine: () => http.post<any>("/api/trikot/me/collect"),
    // Staff-only — collects someone else's by pickup code.
    collect: (code: string) => http.post<any>("/api/trikot/collect", { code }),
  },

  push: {
    vapidPublicKey: () => http.get<{ publicKey: string | null }>("/api/push/vapid-public-key"),
    subscribe: (sub: { endpoint: string; p256dh: string; auth: string }) =>
      http.post("/api/push/subscribe", sub),
    unsubscribe: (endpoint: string) => http.del("/api/push/subscribe", { endpoint }),
    prefs: {
      get: () => http.get<any>("/api/notifications/prefs"),
      put: (data: { pushEnabled?: boolean; emailEnabled?: boolean; hoursBefore?: number; email?: string | null }) =>
        http.put("/api/notifications/prefs", data),
    },
  },

  admin: {
    syncFootball: (live = false) => http.post<any>(`/api/admin/sync-football${live ? "?live=true" : ""}`),
    listUsers: (opts?: { search?: string; limit?: number; offset?: number }) => {
      const q = new URLSearchParams();
      if (opts?.search) q.set("search", opts.search);
      if (opts?.limit != null) q.set("limit", String(opts.limit));
      if (opts?.offset != null) q.set("offset", String(opts.offset));
      const qs = q.toString();
      return http.get<{ users: any[]; total: number; adminCount: number }>(
        `/api/admin/users${qs ? `?${qs}` : ""}`,
      );
    },
    setRole: (userId: string, role: "admin" | "staff" | "user") =>
      http.post(`/api/admin/users/${userId}/role`, { role }),
    removeRole: (userId: string, role: "admin" | "staff" | "user") =>
      http.del(`/api/admin/users/${userId}/role/${role}`),
    settings: {
      list: () => http.get<AdminSetting[]>("/api/admin/settings"),
      reveal: (key: string) =>
        http.get<{ key: string; value: string | null }>(`/api/admin/settings/${encodeURIComponent(key)}/value`),
      put: (key: string, value: string) =>
        http.put(`/api/admin/settings/${encodeURIComponent(key)}`, { value }),
      clear: (key: string) => http.del(`/api/admin/settings/${encodeURIComponent(key)}`),
      generateVapid: () =>
        http.post<{ ok: true; publicKey: string; subject: string }>("/api/admin/settings/generate-vapid"),
    },
    queues: {
      notifications: () =>
        http.get<{
          configured: boolean;
          counts: { waiting: number; active: number; delayed: number; completed: number; failed: number } | null;
        }>("/api/admin/queues/notifications"),
    },
    test: {
      email: (to: string) => http.post<{ ok: true; backend: string; messageId: string }>("/api/admin/test/email", { to }),
      apiFootball: () =>
        http.post<{ ok: true; account: any; subscription: any; requests: any }>("/api/admin/test/api-football"),
      s3: () => http.post<{ ok: true; url: string }>("/api/admin/test/s3"),
      push: () =>
        http.post<{ ok: boolean; sent: number; gone: number; failed: number }>("/api/admin/test/push"),
    },
    metrics: () => http.get<MetricsSnapshot>("/api/admin/metrics"),
    resetForWm: (clearLogs = true) =>
      http.post<{
        ok: true;
        tipsDeleted: number;
        bonusTipsDeleted: number;
        bonusTruthDeleted: number;
        badgesDeleted: number;
        feedDeleted: number;
        insightsDeleted: number;
        testImportGamesDeleted: number;
        profilesReset: number;
        gamesReset: number;
        notificationLogsDeleted: number;
        aiUsageDeleted: number;
      }>("/api/admin/reset-for-wm", { confirm: "RESET", clearLogs }),
    scoring: {
      rules: () => http.get<{ rules: ScoringRules }>("/api/admin/scoring/rules"),
      simulate: (body: { homeScore: number; awayScore: number; tips: SimTip[] }) =>
        http.post<{ homeScore: number; awayScore: number; results: SimResult[] }>("/api/admin/scoring/simulate", body),
      rescore: (gameId: string) =>
        http.post<{ ok: true; tipsUpdated: number; usersUpdated: number }>(
          `/api/admin/scoring/rescore/${gameId}`,
        ),
    },
  },
};

export interface ScoringRules {
  exact: { points: number; xp: number };
  diff: { points: number; xp: number };
  tendency: { points: number; xp: number };
  miss: { points: number; xp: number };
}

export interface SimTip {
  label?: string;
  homeTip: number;
  awayTip: number;
}

export interface SimResult extends SimTip {
  outcome: "exact" | "diff" | "tendency" | "miss";
  points: number;
  xp: number;
  reason: string;
}

export interface MetricsSnapshot {
  activeUsers5m: number;
  series: { minute: number; total: number; s4xx: number; s5xx: number; s429: number; avgMs: number }[];
  replicas: { id: string; sse: number; rssMb: number; heapMb: number; elLagMs: number; uptimeSec: number }[];
  sseTotal: number;
  rlMax: number;
  pool: { total: number; idle: number; waiting: number; max: number };
  redisOk: boolean;
  generatedAt: string;
}

export interface TipSummary {
  total: number;
  home: number;
  draw: number;
  away: number;
  // Score distribution, sorted by count desc. Bounded by distinct scores
  // (~dozens of entries) regardless of tipper count.
  dist: { h: number; a: number; count: number }[];
  myTip: { homeTip: number; awayTip: number; pointsEarned: number; xpEarned: number } | null;
}

export type AtProgression = "group" | "r32" | "r16" | "qf" | "sf" | "final_loser" | "winner";

export interface AtBonusFields {
  atBetterThanDe?: boolean | null;
  atGroupPosition?: number | null;
  atFirstGoalScorer?: string | null;
  atTopScorer?: string | null;
  atProgression?: AtProgression | null;
  atGoalsTotal?: number | null;
}

export interface BonusTipUpsert extends AtBonusFields {
  worldChampion?: string | null;
  topScorer?: string | null;
  groupWinners?: Record<string, string>;
}

export interface BonusResolve extends AtBonusFields {
  worldChampion?: string | null;
  topScorer?: string | null;
  groupWinners: Record<string, string>;
}

export interface ApiFootballFixture {
  id: number;
  kickoffTime: string;
  statusShort: string | null;
  leagueId: number;
  leagueName: string;
  leagueLogo: string | null;
  round: string;
  season: number;
  homeTeam: string;
  awayTeam: string;
  homeLogo: string | null;
  awayLogo: string | null;
  homeGoals: number | null;
  awayGoals: number | null;
  venue: string | null;
  alreadyImported: boolean;
}

export interface LiveBonus {
  hasLive: boolean;
  liveGames: {
    id: string;
    gameNo: number;
    homeTeam: string;
    awayTeam: string;
    homeFlag: string;
    awayFlag: string;
    liveHomeScore: number | null;
    liveAwayScore: number | null;
    minute: number | null;
    statusShort: string | null;
  }[];
  perUser: Record<string, number>;
  perGroup: Record<string, number>;
  generatedAt: string;
}

export interface AdminSetting {
  key: string;
  label: string;
  group: string;
  type: "secret" | "text" | "number";
  hint?: string;
  source: "db" | "env" | "unset";
  isSet: boolean;
  updatedAt: string | null;
}

// ---- SSE helper (replaces supabase.channel) ----
export function subscribeSSE(path: string, handlers: { onMessage?: (data: any) => void; onError?: (e: Event) => void }) {
  const es = new EventSource(path, { withCredentials: true });
  es.onmessage = (e) => {
    try {
      handlers.onMessage?.(JSON.parse(e.data));
    } catch {
      /* skip */
    }
  };
  es.onerror = (e) => handlers.onError?.(e);
  return () => es.close();
}
